export const ROLES = [
  {
    value: 'user',
    label: 'Usuario',
    description: 'Acceso a productos contratados. Sin administración de plataforma.',
    color: 'blue',
  },
  {
    value: 'virtual_agent_chat',
    label: 'Agente Virtual Chat',
    description: 'Acceso a secciones de Agente Virtual Chat.',
    color: 'purple',
  },
  {
    value: 'virtual_agent_voz',
    label: 'Agente Virtual Voz',
    description: 'Acceso a secciones de Agente Virtual Voz.',
    color: 'geekblue',
  },
  {
    value: 'virtual_agent',
    label: 'Agente Virtual (Chat + Voz)',
    description: 'Acceso a Agente Virtual chat y voz.',
    color: 'magenta',
  },
  {
    value: 'admin',
    label: 'Administrador',
    description: 'Acceso a todas las secciones.',
    color: 'red',
  },
];

export const ROLE_ALIASES = {
  coordinador: 'user',
  supervisor: 'user',
  user_flow_virtual_agent: 'virtual_agent',
};

export const normalizeRole = (role) => ROLE_ALIASES[role] || role || 'user';

export const humanizeSlug = (slug) => {
  if (!slug) return '';
  const spaced = String(slug).replace(/[_-]+/g, ' ').trim();
  if (!spaced) return '';
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * Segmentos que son rutas de la app y nunca un id de cuenta.
 *
 * El hubSpotId no siempre es numerico: hay clientes sin portal real de HubSpot
 * cuyo id trae letras (ver isNumericId en utils/accountCrm). Por eso el prefijo
 * de cuenta se reconoce por descarte -- si el primer segmento no esta en esta
 * lista, es una cuenta.
 *
 * IMPORTANTE: al agregar una ruta raiz nueva en AppRouter hay que agregarla aca,
 * si no se va a confundir con un id de cuenta.
 */
export const RESERVED_ROOT_SEGMENTS = new Set([
  'accounts',
  'auth',
  'consumption',
  'home',
  'landing',
  'limits',
  'platform',
  'products',
  'service',
  'users',
]);

/** Rutas globales que nunca llevan prefijo de cuenta. */
export const UNPREFIXED_ROOT_SEGMENTS = new Set([
  'accounts',
  'auth',
  'consumption',
  'landing',
  'platform',
  'products',
]);

/** Un id de cuenta es cualquier segmento suelto que no sea una ruta de la app. */
export const isAccountId = (value) => {
  const str = String(value ?? '').trim();
  if (!str || str.includes('/')) return false;
  return !RESERVED_ROOT_SEGMENTS.has(str.toLowerCase());
};

export const stripAccountPrefix = (path) => {
  if (!path) return '/';
  const str = String(path).trim();
  const withSlash = str.startsWith('/') ? str : `/${str}`;
  const [, first = ''] = withSlash.split('/');
  if (!isAccountId(first)) return withSlash || '/';
  return withSlash.slice(first.length + 1) || '/';
};

export const PLATFORM_SECTIONS = [
  {
    id: 'platform.users',
    group: 'Plataforma',
    productId: null,
    label: 'Usuarios',
    pathPrefixes: ['/users'],
  },
  {
    id: 'platform.accounts',
    group: 'Plataforma',
    productId: null,
    label: 'Cuentas',
    pathPrefixes: ['/accounts'],
  },
  {
    id: 'platform.products',
    group: 'Plataforma',
    productId: null,
    label: 'Productos',
    pathPrefixes: ['/products'],
  },
  {
    id: 'platform.consumption',
    group: 'Plataforma',
    productId: null,
    label: 'Consumo de Clientes',
    pathPrefixes: ['/consumption'],
  },
  {
    id: 'platform.targets',
    group: 'Plataforma',
    productId: null,
    label: 'Metas CSM',
    pathPrefixes: ['/consumption/targets'],
  },
  {
    id: 'platform.audit',
    group: 'Plataforma',
    productId: null,
    label: 'Auditoría',
    pathPrefixes: ['/platform/audit'],
  },
  {
    id: 'platform.incidents',
    group: 'Plataforma',
    productId: null,
    label: 'Incidentes',
    pathPrefixes: ['/platform/incidents'],
  },
];

/**
 * Sección que NO sigue la regla de «el personal de plataforma lo ve todo»: el centro de
 * incidentes cruza todos los clientes y solo lo ve quien tenga este permiso, activado a
 * mano por persona (Usuarios) o por rol. Nace APAGADA para todos los roles, el admin
 * incluido. No hace falta ser backoffice para verlo, pero solo backoffice puede
 * otorgarlo. La decisión final es de la API; ver `useIncidentsAccess`.
 */
export const INCIDENTS_SECTION = 'platform.incidents';

// Grupo propio de la bandeja de entrada en la matriz de permisos: ya no es parte de WhatsApp.
// Los ids siguen siendo `whatsapp.*` porque ya están guardados en perfiles, usuarios y backend.
export const INBOX_GROUP = 'Bandeja de entrada';

export const PERMISSION_SECTIONS = [
  ...PLATFORM_SECTIONS,
  // Deliberadamente fuera de PRODUCT_ALLOWED, mismo motivo que
  // `agent-manager.ocupacion`: nace denegada para el rol `user` y se
  // habilita cliente por cliente con un override en `custom:permissions` —
  // no todos los clientes deben ver el consumo de sus bolsas.
  {
    id: 'limits',
    group: 'Límites',
    productId: null,
    productIds: ['NTHSSMS', 'NTHSVOI', 'NTHW', 'NTVA_CHAT', 'NTVA_VOZ'],
    label: 'Límites',
    pathPrefixes: ['/limits'],
  },
  {
    id: 'sms.hub',
    group: 'SMS',
    productId: 'NTHSSMS',
    label: 'SMS',
    pathPrefixes: ['/service/sms'],
  },
  {
    id: 'sms.consumos',
    group: 'SMS',
    productId: 'NTHSSMS',
    label: 'Consumos',
    pathPrefixes: ['/service/sms/consumos'],
  },
  {
    id: 'sms.reportes',
    group: 'SMS',
    productId: 'NTHSSMS',
    label: 'Reportes',
    pathPrefixes: ['/service/sms/reportes'],
  },
  {
    id: 'call-blasting.hub',
    group: 'Call Blasting',
    productId: 'NTHSVOI',
    label: 'Call Blasting',
    pathPrefixes: ['/service/call-blasting'],
  },
  {
    id: 'call-blasting.consumos',
    group: 'Call Blasting',
    productId: 'NTHSVOI',
    label: 'Consumos',
    pathPrefixes: ['/service/call-blasting/consumos'],
  },
  {
    id: 'call-blasting.reportes',
    group: 'Call Blasting',
    productId: 'NTHSVOI',
    label: 'Reportes',
    pathPrefixes: ['/service/call-blasting/reportes'],
  },
  {
    id: 'agent-manager.hub',
    group: 'Agent Manager',
    productId: 'NTAM',
    label: 'Agent Manager',
    pathPrefixes: ['/service/agent-manager'],
  },
  {
    id: 'agent-manager.colas',
    group: 'Agent Manager',
    productId: 'NTAM',
    label: 'Colas',
    pathPrefixes: ['/service/agent-manager/colas'],
  },
  {
    id: 'agent-manager.reportes',
    group: 'Agent Manager',
    productId: 'NTAM',
    label: 'Reportes',
    pathPrefixes: ['/service/agent-manager/reportes'],
  },
  // Deliberadamente fuera de PRODUCT_ALLOWED: nace denegada para el rol `user`
  // y se habilita cliente por cliente con un override en `custom:permissions`.
  // Es un desarrollo pedido por una sola cuenta, y así se evita comprobar el
  // portalId dentro del código.
  {
    id: 'agent-manager.ocupacion',
    group: 'Agent Manager',
    productId: 'NTAM',
    label: 'Ocupación de agentes',
    pathPrefixes: ['/service/agent-manager/ocupacion'],
  },
  // Fuera de PRODUCT_ALLOWED por el mismo motivo que la de arriba: nace denegada
  // para el rol `user` y se habilita cliente por cliente con un override en
  // `custom:permissions`. Va aparte de `ocupacion` a propósito — son reportes
  // distintos y un cliente puede querer uno sin el otro.
  {
    id: 'agent-manager.llamadas-extension',
    group: 'Agent Manager',
    productId: 'NTAM',
    label: 'Llamadas por extensión',
    pathPrefixes: ['/service/agent-manager/llamadas-extension'],
  },
  {
    id: 'virtual-agent.hub',
    group: 'Agente Virtual',
    productId: null,
    productIds: ['NTVA_CHAT', 'NTVA_VOZ', 'NTRV'],
    label: 'Agente Virtual',
    pathPrefixes: ['/service/virtual-agent'],
  },
  {
    id: 'virtual-agent.voz',
    group: 'Agente Virtual',
    productId: 'NTVA_VOZ',
    label: 'Voz',
    pathPrefixes: ['/service/virtual-agent/voz'],
  },
  {
    id: 'virtual-agent.voz.flows',
    group: 'Agente Virtual',
    productId: 'NTVA_VOZ',
    label: 'Flujos de voz',
    pathPrefixes: ['/service/virtual-agent/voz/flows'],
  },
  {
    id: 'virtual-agent.voz.conversaciones',
    group: 'Agente Virtual',
    productId: 'NTVA_VOZ',
    label: 'Conversaciones de voz',
    pathPrefixes: ['/service/virtual-agent/voz/conversaciones'],
  },
  {
    id: 'virtual-agent.voz.consumos',
    group: 'Agente Virtual',
    productId: 'NTVA_VOZ',
    label: 'Consumos de voz',
    pathPrefixes: ['/service/virtual-agent/voz/consumos'],
  },
  {
    id: 'virtual-agent.voz.reportes',
    group: 'Agente Virtual',
    productId: 'NTVA_VOZ',
    label: 'Reportes de voz',
    pathPrefixes: ['/service/virtual-agent/voz/reportes'],
  },
  {
    id: 'virtual-agent.chat',
    group: 'Agente Virtual',
    productId: 'NTVA_CHAT',
    label: 'Chat',
    pathPrefixes: ['/service/virtual-agent/chat'],
  },
  {
    id: 'virtual-agent.chat.flows',
    group: 'Agente Virtual',
    productId: 'NTVA_CHAT',
    label: 'Flujos de chat',
    pathPrefixes: ['/service/virtual-agent/chat/flows'],
  },
  {
    id: 'virtual-agent.chat.consumos',
    group: 'Agente Virtual',
    productId: 'NTVA_CHAT',
    label: 'Consumos de chat',
    pathPrefixes: ['/service/virtual-agent/chat/consumos'],
  },
  {
    id: 'virtual-agent.chat.reportes',
    group: 'Agente Virtual',
    productId: 'NTVA_CHAT',
    label: 'Reportes de chat',
    pathPrefixes: ['/service/virtual-agent/chat/reportes'],
  },
  {
    id: 'virtual-agent.chat.seguimiento',
    group: 'Agente Virtual',
    productId: 'NTVA_CHAT',
    label: 'Seguimiento de pasos',
    pathPrefixes: ['/service/virtual-agent/chat/seguimiento-pasos'],
  },
  {
    id: 'whatsapp.hub',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'WhatsApp',
    pathPrefixes: ['/service/whatsapp'],
  },
  {
    id: 'whatsapp.phones',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Mis números y plantillas',
    pathPrefixes: ['/service/whatsapp/phones'],
  },
  {
    id: 'whatsapp.active-chats',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Chats activos',
    pathPrefixes: ['/service/whatsapp/active-chats'],
  },
  {
    id: 'whatsapp.historical-chats',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Chats históricos',
    pathPrefixes: ['/service/whatsapp/historical-chats'],
  },
  {
    // Ver el historial de lotes y su detalle. Crear envíos es `whatsapp.bulk-sends.send`.
    id: 'whatsapp.bulk-sends',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Envíos masivos',
    pathPrefixes: ['/service/whatsapp/bulk-sends'],
  },
  {
    // Más específico que `whatsapp.bulk-sends`: la pantalla Nuevo envío (y su botón en el
    // historial) la protege este permiso, que implica poder ver la sección.
    id: 'whatsapp.bulk-sends.send',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Enviar plantillas',
    pathPrefixes: ['/service/whatsapp/bulk-sends/new'],
  },
  {
    // Permiso de ACCION, no de ruta: en la bandeja nativa, con el canal en
    // "Privacidad de tickets cerrados", deja ver todos los cerrados y no solo
    // los que el usuario atendio. Los administradores lo tienen siempre. El
    // prefijo es virtual (ninguna pagina lo usa): hace falta uno para que el
    // orden por ruta de SORTED_SECTIONS_BY_PATH no reciba un arreglo vacio.
    id: 'whatsapp.closed-viewall',
    group: INBOX_GROUP,
    productId: 'NTHW',
    label: 'Ver todos los tickets cerrados',
    pathPrefixes: ['/service/whatsapp/__permisos/closed-viewall'],
  },
  {
    id: 'whatsapp.inbox',
    group: INBOX_GROUP,
    productId: 'NTHW',
    label: 'Chats',
    // Sus propias conversaciones abiertas y las que esperan asesor. La bandeja vive bajo
    // Social Media: estos prefijos tienen que ser más específicos que `/service/social-media`
    // (sección `social-media`), o cualquiera con acceso a Social Media entraría a la
    // reportería y al catálogo sin sus permisos.
    pathPrefixes: ['/service/social-media/inbox-hub', '/service/social-media/inbox'],
  },
  {
    // Permiso de ACCION, no de ruta (prefijo virtual, igual que closed-viewall).
    id: 'whatsapp.inbox-all',
    group: INBOX_GROUP,
    productId: 'NTHW',
    label: 'Ver las conversaciones de todos los asesores',
    pathPrefixes: ['/service/whatsapp/__permisos/inbox-all'],
  },
  {
    // Permiso de ACCION: muestra la pestaña Cerradas de la bandeja.
    id: 'whatsapp.inbox-closed',
    group: INBOX_GROUP,
    productId: 'NTHW',
    label: 'Ver chats cerrados',
    pathPrefixes: ['/service/whatsapp/__permisos/inbox-closed'],
  },
  {
    id: 'whatsapp.inbox-report',
    group: INBOX_GROUP,
    productId: 'NTHW',
    label: 'Reportería',
    pathPrefixes: ['/service/social-media/inbox-report'],
  },
  {
    id: 'whatsapp.typification',
    group: INBOX_GROUP,
    productId: 'NTHW',
    label: 'Catálogo de tipificación',
    pathPrefixes: ['/service/social-media/typification-catalog'],
  },
  {
    id: 'whatsapp.metrics',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Métricas',
    pathPrefixes: ['/service/whatsapp/metrics', '/service/whatsapp/limits'],
  },
  {
    id: 'whatsapp.catalog',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Catálogo',
    pathPrefixes: ['/service/whatsapp/catalog'],
  },
  {
    id: 'nthw-templates',
    group: 'WhatsApp',
    productId: 'NTHW',
    label: 'Plantillas',
    pathPrefixes: ['/service/nthw-templates'],
  },
  {
    id: 'portafolio',
    group: 'Portafolio',
    productId: 'NTP',
    label: 'Portafolio',
    pathPrefixes: ['/service/portafolio'],
  },
  {
    id: 'integraciones',
    group: 'Integraciones',
    productId: 'NTI',
    label: 'Integraciones',
    pathPrefixes: ['/service/integraciones'],
  },
  {
    id: 'chat-bot',
    group: 'Chat Bot',
    productId: 'NTU',
    label: 'Chat Bot',
    pathPrefixes: ['/service/chat-bot'],
  },
  {
    id: 'agendador',
    group: 'Agendador',
    productId: 'NTS',
    label: 'Agendador',
    pathPrefixes: ['/service/agendador'],
  },
  {
    id: 'recepcionista',
    group: 'Recepcionista virtual',
    productId: 'NTRV',
    label: 'Recepcionista virtual',
    pathPrefixes: ['/service/recepcionista'],
  },
  {
    id: 'phone-validator',
    group: 'Phone Validator',
    productId: 'NTPV',
    label: 'Phone Validator',
    pathPrefixes: ['/service/phone-validator'],
  },
  {
    id: 'phone-validator.properties',
    group: 'Phone Validator',
    productId: 'NTPV',
    label: 'Propiedades',
    pathPrefixes: ['/service/phone-validator/properties'],
  },
  {
    // Más específico que `social-media` (la sección que sigue): sin esto, quien tiene Social Media
    // entraría a la reportería del widget sin su permiso. Ver `whatsapp.inbox-report`.
    id: 'social-media.widget-report',
    group: 'Social Media',
    productId: 'NTHW',
    label: 'Reportería del widget',
    pathPrefixes: ['/service/social-media/widget/report'],
  },
  {
    // Más específico que `social-media`: la bandeja también vive bajo Social Media, así que sin
    // esto cualquiera con acceso a la bandeja (p. ej. un asesor) administraría los widgets.
    id: 'social-media.widget',
    group: 'Social Media',
    productId: 'NTHW',
    label: 'Widgets',
    pathPrefixes: ['/service/social-media/widget'],
  },
  {
    id: 'social-media',
    group: 'Social Media',
    productId: 'NTHW',
    label: 'Social Media',
    pathPrefixes: ['/service/social-media'],
  },
  {
    id: 'virtual-auditors',
    group: 'Auditores virtuales',
    productId: 'NTVAA',
    label: 'Auditores virtuales',
    pathPrefixes: ['/service/virtual-auditors'],
  },
];

const SECTION_IDS = PERMISSION_SECTIONS.map((s) => s.id);

export const getProductIdsForSection = (section) => {
  if (!section) return [];
  if (Array.isArray(section.productIds) && section.productIds.length) return section.productIds;
  if (section.productId) return [section.productId];
  return [];
};

const normalizePath = (value) => stripAccountPrefix(value).toLowerCase();

export const normalizeAccessValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0' || value === 'none') {
    return false;
  }
  if (value === 'view' || value === 'edit' || value === 'manage') return true;
  return false;
};

const ALL_ALLOWED = SECTION_IDS.reduce((acc, id) => {
  acc[id] = true;
  return acc;
}, {});

const PLATFORM_DENIED = {
  'platform.users': false,
  'platform.accounts': false,
  'platform.products': false,
  'platform.consumption': false,
  'platform.targets': false,
  'platform.audit': false,
  'platform.incidents': false,
};

const PRODUCT_ALLOWED = {
  'sms.hub': true,
  'sms.consumos': true,
  'sms.reportes': true,
  'call-blasting.hub': true,
  'call-blasting.consumos': true,
  'call-blasting.reportes': true,
  'agent-manager.hub': true,
  'agent-manager.colas': true,
  'agent-manager.reportes': true,
  'virtual-agent.hub': true,
  'virtual-agent.voz': true,
  'virtual-agent.voz.flows': false,
  'virtual-agent.voz.conversaciones': true,
  'virtual-agent.voz.consumos': true,
  'virtual-agent.voz.reportes': true,
  'virtual-agent.chat': true,
  'virtual-agent.chat.flows': true,
  'virtual-agent.chat.consumos': true,
  'virtual-agent.chat.reportes': true,
  'virtual-agent.chat.seguimiento': true,
  'whatsapp.hub': true,
  'whatsapp.phones': true,
  'whatsapp.active-chats': false,
  'whatsapp.historical-chats': true,
  // Los envíos masivos son solo de administradores por ahora; se pueden dar por perfil en la
  // matriz (ver el historial y, aparte, enviar plantillas).
  'whatsapp.bulk-sends': false,
  'whatsapp.bulk-sends.send': false,
  'whatsapp.metrics': true,
  'whatsapp.catalog': false,
  // Bandeja de entrada: quien ya era "Usuario" sigue viendo lo mismo que antes
  // (chats, los de todos y Cerradas). Reportería y catálogo de tipificación eran solo de
  // administradores; se pueden dar por usuario desde la matriz de permisos.
  'whatsapp.inbox': true,
  'whatsapp.inbox-all': true,
  'whatsapp.inbox-closed': true,
  'whatsapp.inbox-report': false,
  'whatsapp.typification': false,
  'nthw-templates': false,
  'portafolio': true,
  'integraciones': true,
  'chat-bot': true,
  'agendador': true,
  'recepcionista': true,
  'phone-validator': true,
  'phone-validator.properties': true,
  'social-media': true,
  // El widget web es solo de administradores por ahora; se puede dar por perfil en la matriz.
  'social-media.widget': false,
  'social-media.widget-report': false,
  // Visible para el usuario, pero la seccion sigue detras del producto NTVAA (RequireProduct).
  'virtual-auditors': true,
};

const VIRTUAL_AGENT_CHAT_TEMPLATE = {
  ...PLATFORM_DENIED,
  ...PRODUCT_ALLOWED,
  'virtual-agent.hub': true,
  'virtual-agent.voz': false,
  'virtual-agent.voz.flows': false,
  'virtual-agent.voz.conversaciones': false,
  'virtual-agent.voz.consumos': false,
  'virtual-agent.voz.reportes': false,
  'virtual-agent.chat': true,
  'virtual-agent.chat.flows': true,
  'virtual-agent.chat.consumos': true,
  'virtual-agent.chat.reportes': true,
  'virtual-agent.chat.seguimiento': true,
};

const VIRTUAL_AGENT_VOZ_TEMPLATE = {
  ...PLATFORM_DENIED,
  ...PRODUCT_ALLOWED,
  'virtual-agent.hub': true,
  'virtual-agent.voz': true,
  'virtual-agent.voz.flows': true,
  'virtual-agent.voz.conversaciones': true,
  'virtual-agent.voz.consumos': true,
  'virtual-agent.voz.reportes': true,
  'virtual-agent.chat': false,
  'virtual-agent.chat.flows': false,
  'virtual-agent.chat.consumos': false,
  'virtual-agent.chat.reportes': false,
  'virtual-agent.chat.seguimiento': false,
};

const VIRTUAL_AGENT_FULL_TEMPLATE = {
  ...PLATFORM_DENIED,
  ...PRODUCT_ALLOWED,
  'virtual-agent.hub': true,
  'virtual-agent.voz': true,
  'virtual-agent.voz.flows': true,
  'virtual-agent.voz.conversaciones': true,
  'virtual-agent.voz.consumos': true,
  'virtual-agent.voz.reportes': true,
  'virtual-agent.chat': true,
  'virtual-agent.chat.flows': true,
  'virtual-agent.chat.consumos': true,
  'virtual-agent.chat.reportes': true,
  'virtual-agent.chat.seguimiento': true,
};

export const ROLE_PERMISSIONS = {
  // `platform.incidents` es la excepción: nace apagada incluso para admin. La matriz de
  // permisos solo guarda diferencias frente al defecto del rol; con admin en `true` un «Sí»
  // nunca se guardaría y la API (que exige el permiso explícito) lo vería como denegado.
  admin: { ...ALL_ALLOWED, [INCIDENTS_SECTION]: false },
  user: { ...PLATFORM_DENIED, ...PRODUCT_ALLOWED },
  virtual_agent_chat: VIRTUAL_AGENT_CHAT_TEMPLATE,
  virtual_agent_voz: VIRTUAL_AGENT_VOZ_TEMPLATE,
  virtual_agent: VIRTUAL_AGENT_FULL_TEMPLATE,
};

// Valores por defecto de los permisos de la bandeja cuando el mapa del usuario no los trae
// (un backend anterior a ellos, o un rol guardado antes de que existieran). Son lo que ya
// tenía quien era "Usuario": los chats, los de todos y Cerradas. Es el mismo valor que usa el
// backend (INBOX_COMPAT_DEFAULTS en permissions.ts); si cambia uno, hay que cambiar el otro.
export const INBOX_COMPAT_DEFAULTS = {
  'whatsapp.inbox': true,
  'whatsapp.inbox-all': true,
  'whatsapp.inbox-closed': true,
  'whatsapp.inbox-report': false,
  'whatsapp.typification': false,
};

// Perfiles cuyas demás secciones de WhatsApp (números, historial, métricas...) también se
// ocultan en el menú y por ruta. Esas secciones hoy solo ocultan el menú lateral y los demás
// roles no las obedecen, así que aplicarlas a todos les quitaría tarjetas que ya ven. Los
// permisos de la bandeja NO dependen de esta lista: el backend los hace cumplir para todos.
// El perfil lo crea el administrador en /platform/profiles; aquí ya no se define.
export const ENFORCED_ROLES = ['asesor'];

export const isEnforcedRole = (role) => ENFORCED_ROLES.includes(normalizeRole(role));

export const VIRTUAL_AGENT_ROLES = [
  'virtual_agent',
  'virtual_agent_chat',
  'virtual_agent_voz',
  'user_flow_virtual_agent',
];

export const isVirtualAgentRole = (role) =>
  VIRTUAL_AGENT_ROLES.includes(role) || VIRTUAL_AGENT_ROLES.includes(normalizeRole(role));

const SORTED_SECTIONS_BY_PATH = [...PERMISSION_SECTIONS].sort((a, b) => {
  const maxA = Math.max(...a.pathPrefixes.map((p) => p.length));
  const maxB = Math.max(...b.pathPrefixes.map((p) => p.length));
  return maxB - maxA;
});

const findRemoteProfile = (role, profiles = []) =>
  (profiles || []).find(
    (item) => item?.profileSlug === role || item?.roleSlug === role
  );

export const getRoleMeta = (role, profiles = []) => {
  const remote = findRemoteProfile(role, profiles);
  if (remote) {
    const slug = remote.profileSlug || remote.roleSlug || role;
    return {
      value: role,
      label: remote.label || humanizeSlug(slug),
      description: remote.description || '',
      color: remote.color || 'default',
    };
  }

  const exact = ROLES.find((r) => r.value === role);
  if (exact) return exact;

  const normalized = normalizeRole(role);
  const fromAlias = ROLES.find((r) => r.value === normalized);
  if (fromAlias) {
    return {
      ...fromAlias,
      value: role,
      label: `${fromAlias.label} (legado)`,
      description: '',
    };
  }

  return {
    value: role,
    label: role,
    description: '',
    color: 'default',
  };
};

export const buildDefaultPermissionsForRole = (role, profiles = []) => {
  const remote = findRemoteProfile(role, profiles);
  if (remote?.permissions) {
    return SECTION_IDS.reduce((acc, id) => {
      acc[id] = normalizeAccessValue(remote.permissions[id]);
      return acc;
    }, {});
  }

  const normalized = normalizeRole(role);
  const source = ROLE_PERMISSIONS[normalized] || {};
  return SECTION_IDS.reduce((acc, id) => {
    acc[id] = normalizeAccessValue(source[id]);
    return acc;
  }, {});
};

export const hasSectionAccess = (role, sectionId, overrides = null) => {
  const normalized = normalizeRole(role);
  const rolePerms = ROLE_PERMISSIONS[normalized] || {};
  let allowed = normalizeAccessValue(rolePerms[sectionId]);
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, sectionId)) {
    allowed = normalizeAccessValue(overrides[sectionId]);
  }
  return allowed;
};

export const getAccessLevel = (role, sectionId, overrides = null) =>
  hasSectionAccess(role, sectionId, overrides);

export const resolveSectionIdFromPath = (pathname) => {
  if (!pathname) return null;
  const lowerPath = normalizePath(pathname);

  for (const section of SORTED_SECTIONS_BY_PATH) {
    for (const prefix of section.pathPrefixes) {
      const lowerPrefix = prefix.toLowerCase();
      if (lowerPath === lowerPrefix || lowerPath.startsWith(`${lowerPrefix}/`)) {
        return section.id;
      }
    }
  }
  return null;
};

export const canAccessPath = (role, pathname, overrides = null) => {
  const sectionId = resolveSectionIdFromPath(pathname);
  if (!sectionId) {
    return true;
  }
  return hasSectionAccess(role, sectionId, overrides);
};

/**
 * Resolves section access for the active account.
 * Platform staff (backoffice / platformAdmin / Cognito admin) always pass.
 * Otherwise role defaults apply; an empty permissions map is not a deny-all.
 */
export const canAccessSection = ({
  sectionId,
  canAccessAllAccounts = false,
  role,
  permissions,
} = {}) => {
  if (!sectionId) return true;
  if (canAccessAllAccounts) return true;
  return hasSectionAccess(role, sectionId, permissions);
};

export const normalizePermissionsMap = (raw = {}) =>
  Object.entries(raw || {}).reduce((acc, [sectionId, value]) => {
    acc[sectionId] = normalizeAccessValue(value);
    return acc;
  }, {});

export const parsePermissionsAttribute = (raw) => {
  if (!raw || raw === '{}' || raw === 'null') {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      if (parsed.role || parsed.r) {
        return normalizePermissionsMap(parsed.permissions || parsed.p || {});
      }
      return normalizePermissionsMap(parsed);
    }
    return {};
  } catch {
    return {};
  }
};

export const serializePermissionsAttribute = (overrides) => {
  if (!overrides || typeof overrides !== 'object') {
    return '{}';
  }
  return JSON.stringify(normalizePermissionsMap(overrides));
};

export const parseProfileAttribute = (raw) => {
  const fallback = { role: 'user', overrides: {} };
  if (raw == null || raw === '') {
    return fallback;
  }

  const trimmed = String(raw).trim();
  if (!trimmed) {
    return fallback;
  }

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return {
          role: normalizeRole(parsed.role || parsed.r || 'user'),
          overrides: normalizePermissionsMap(parsed.permissions || parsed.p || {}),
        };
      }
    } catch {
      return fallback;
    }
  }

  return {
    role: normalizeRole(trimmed),
    overrides: {},
  };
};

export const serializeProfileAttribute = (role) => normalizeRole(role);

export const isCognitoPlatformAdmin = (user) =>
  parseProfileAttribute(user?.attributes?.profile).role === 'admin';

export const canAccessAllAccounts = ({
  backoffice = false,
  platformAdmin = false,
  user = null,
} = {}) =>
  Boolean(backoffice || platformAdmin || isCognitoPlatformAdmin(user));

// `profiles` son los perfiles de plataforma: sin ellos un rol que no es fijo (p. ej. `asesor`)
// resuelve a "todo apagado" y las diferencias contra los valores del perfil salen mal.
export const permissionsMatchRoleDefaults = (role, permissions, profiles = []) => {
  const defaults = buildDefaultPermissionsForRole(role, profiles);
  return SECTION_IDS.every(
    (id) =>
      normalizeAccessValue(permissions?.[id] ?? defaults[id]) ===
      normalizeAccessValue(defaults[id])
  );
};

export const mergePermissionsWithRoleDefaults = (role, overrides = {}, profiles = []) => {
  const defaults = buildDefaultPermissionsForRole(role, profiles);
  const merged = { ...defaults };
  Object.entries(normalizePermissionsMap(overrides)).forEach(([sectionId, allowed]) => {
    if (SECTION_IDS.includes(sectionId)) {
      merged[sectionId] = allowed;
    }
  });
  return merged;
};

/**
 * `profiles` es el catálogo REAL de roles (perfiles globales y roles de la cuenta). Sin él,
 * los defaults salen de la tabla fija de este archivo, que puede no coincidir con lo que
 * el rol realmente trae: el editor mostraría apagado un permiso que el rol da.
 */
export const extractPermissionOverrides = (role, permissions, profiles = []) => {
  const defaults = buildDefaultPermissionsForRole(role, profiles);
  return Object.entries(permissions || {}).reduce((acc, [sectionId, value]) => {
    const allowed = normalizeAccessValue(value);
    if (allowed !== normalizeAccessValue(defaults[sectionId])) {
      acc[sectionId] = allowed;
    }
    return acc;
  }, {});
};

const INBOX_SECTION_IDS = new Set(
  PERMISSION_SECTIONS.filter((section) => section.group === INBOX_GROUP).map((section) => section.id)
);

export const buildMenuPermissionSections = (products = []) => {
  const sections = [...PLATFORM_SECTIONS];
  const seen = new Set(sections.map((s) => s.id));

  (products || []).forEach((product) => {
    const group = product.comercialName || product.name || product.id || 'Producto';
    (product.views || []).forEach((view) => {
      const path = normalizePath(view.path);
      const id = resolveSectionIdFromPath(path);
      if (!id || seen.has(id)) return;
      // Los de la bandeja se definen en PERMISSION_SECTIONS: con la vista del producto saldrían
      // dentro del grupo del producto y no en el suyo.
      if (INBOX_SECTION_IDS.has(id)) return;
      seen.add(id);
      sections.push({
        id,
        group,
        productId: product.id || product.productId || null,
        label: view.name || path,
        pathPrefixes: [path],
      });
    });
  });

  PERMISSION_SECTIONS.forEach((section) => {
    if (seen.has(section.id)) return;
    seen.add(section.id);
    sections.push(section);
  });

  return sections;
};
